/**
 * AnnotationSensor — lives inside the R3F Canvas.
 *
 * Every frame it:
 *   1. Finds the actual THREE.Mesh for each annotated geometry by UUID.
 *   2. Uses the mesh's matrixWorld to transform bbox corners to world space.
 *   3. Projects corners to 2D screen space via the camera.
 *   4. Clips the 2D bbox to the viewport and computes visibility ratio.
 *   5. Tracks hover state for geometries with alwaysVisible=false.
 *   6. Detects camera movement and hides annotations while moving, then
 *      shows them again 200ms after the camera stops.
 *
 * Movement detection has two mechanisms:
 *
 *   a) **OrbitControls events** (`start` / `end`) — the primary signal for
 *      user-initiated interaction (orbit, pan, zoom).  The `end` event fires
 *      on mouseup/touchend *before* damping finishes, so annotations reappear
 *      promptly without waiting for damping to settle.
 *
 *   b) **Matrix comparison** — a fallback for programmatic camera changes
 *      (auto-rotate in presentation mode, PiP camera animation) that don't
 *      fire OrbitControls events.  Uses a generous epsilon (1e-2) so
 *      residual damping noise is ignored.
 */

import { useRef, useEffect, useCallback } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import type { ColoredGeometry } from '@/components/viewer/types';
import type { AnnotationRenderData } from './annotationLayout';

/** Minimum visibility fraction (0–1) to consider an annotation "visible enough". */
const VISIBILITY_THRESHOLD = 0.25;

/** Delay after interaction ends / camera settles before annotations reappear. */
const SHOW_AFTER_STOP_MS = 200;

/**
 * Epsilon for the programmatic-movement matrix check.
 *
 * This only needs to catch large intentional camera changes (animation,
 * auto-rotate) — not OrbitControls damping residuals.  A generous value
 * avoids false positives from damping while still detecting real moves.
 */
const MATRIX_EPS = 1e-2;

/**
 * Epsilon-based Matrix4 comparison.  Returns true when every element of
 * `a` and `b` differs by less than `eps`.
 */
function matricesNearlyEqual(
  a: THREE.Matrix4,
  b: THREE.Matrix4,
  eps: number,
): boolean {
  const ae = a.elements;
  const be = b.elements;
  for (let i = 0; i < 16; i++) {
    if (Math.abs(ae[i] - be[i]) > eps) return false;
  }
  return true;
}

interface AnnotationSensorProps {
  annotatedGeometries: ColoredGeometry[];
  enabled: boolean;
  modelGroupRef: React.RefObject<THREE.Group | null>;
  onDataChange: (data: AnnotationRenderData[]) => void;
  onMovingChange: (moving: boolean) => void;
}

export function AnnotationSensor({
  annotatedGeometries,
  enabled,
  modelGroupRef,
  onDataChange,
  onMovingChange,
}: AnnotationSensorProps) {
  const { camera, size, get } = useThree();
  const { gl } = useThree();
  const controls = useThree((state) => state.controls);

  // ---- Movement state ----
  const prevCamMatrix = useRef(new THREE.Matrix4());
  const isMovingRef = useRef(false);
  const stopTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // True between OrbitControls `start` and `end` events (user is dragging).
  const userInteractingRef = useRef(false);

  // Stable callback refs — always current without causing hook re-runs
  const onMovingChangeRef = useRef(onMovingChange);
  onMovingChangeRef.current = onMovingChange;

  const onDataChangeRef = useRef(onDataChange);
  onDataChangeRef.current = onDataChange;

  // `get` from useThree always returns the live R3F store — safe to call
  // from setTimeout without stale-closure issues.
  const getRef = useRef(get);
  getRef.current = get;

  // ---- Shared helpers for entering / leaving "moving" state ----

  /** Transition into the "moving" state — hides annotations immediately. */
  const enterMoving = useCallback(() => {
    if (isMovingRef.current) return;
    isMovingRef.current = true;
    onMovingChangeRef.current(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refs only
    prevDataRef.current = '__moving__';
    onDataChangeRef.current([]);
  }, []);

  /** Schedule the transition out of "moving" after SHOW_AFTER_STOP_MS. */
  const scheduleStopTimer = useCallback(() => {
    if (stopTimerRef.current) clearTimeout(stopTimerRef.current);
    stopTimerRef.current = setTimeout(() => {
      stopTimerRef.current = null;
      isMovingRef.current = false;
      prevDataRef.current = ''; // force fresh push on next frame
      onMovingChangeRef.current(false);
      // Request a new frame — works with frameloop="demand"
      getRef.current().invalidate();
    }, SHOW_AFTER_STOP_MS);
  }, []);

  // ---- OrbitControls event listeners ----
  useEffect(() => {
    if (!controls) return;
    const ctrl = controls as THREE.EventDispatcher;

    const onStart = () => {
      userInteractingRef.current = true;
      // Cancel any pending "show annotations" timer from a previous gesture
      if (stopTimerRef.current) {
        clearTimeout(stopTimerRef.current);
        stopTimerRef.current = null;
      }
      enterMoving();
    };

    const onEnd = () => {
      userInteractingRef.current = false;
      // User released the mouse — schedule annotations to reappear.
      // Damping may still be running but we intentionally ignore it.
      scheduleStopTimer();
    };

    ctrl.addEventListener('start', onStart);
    ctrl.addEventListener('end', onEnd);
    return () => {
      ctrl.removeEventListener('start', onStart);
      ctrl.removeEventListener('end', onEnd);
    };
  }, [controls, enterMoving, scheduleStopTimer]);

  // ---- Geometry UUID → mesh lookup cache ----
  const meshCacheRef = useRef<Map<string, THREE.Mesh>>(new Map());

  // ---- Bounding box cache per UUID ----
  const bboxCache = useRef<Map<string, THREE.Box3>>(new Map());

  // ---- Previous serialised data to skip unchanged pushes ----
  const prevDataRef = useRef<string>('');

  // ---- Hover tracking ----
  const raycaster = useRef(new THREE.Raycaster());
  const pointerRef = useRef(new THREE.Vector2(-9999, -9999));

  const handlePointerMove = useCallback(
    (e: PointerEvent) => {
      if (!enabled) return;
      const rect = gl.domElement.getBoundingClientRect();
      pointerRef.current.set(
        ((e.clientX - rect.left) / rect.width) * 2 - 1,
        -((e.clientY - rect.top) / rect.height) * 2 + 1,
      );
    },
    [enabled, gl.domElement],
  );

  useEffect(() => {
    const el = gl.domElement;
    el.addEventListener('pointermove', handlePointerMove);
    return () => el.removeEventListener('pointermove', handlePointerMove);
  }, [gl.domElement, handlePointerMove]);

  // ---- Rebuild caches when geometries change ----
  useEffect(() => {
    meshCacheRef.current.clear();
    bboxCache.current.clear();
  }, [annotatedGeometries]);

  // ---- Cleanup timer on unmount ----
  useEffect(() => {
    return () => {
      if (stopTimerRef.current) clearTimeout(stopTimerRef.current);
    };
  }, []);

  // -------------------------------------------------------------------
  // Find mesh by geometry UUID — cached after first traversal
  // -------------------------------------------------------------------
  const findMeshByUUID = useCallback(
    (uuid: string): THREE.Mesh | null => {
      const cached = meshCacheRef.current.get(uuid);
      if (cached) return cached;
      if (!modelGroupRef.current) return null;
      let found: THREE.Mesh | null = null;
      modelGroupRef.current.traverse((obj) => {
        if (found) return;
        if ((obj as THREE.Mesh).isMesh) {
          const mesh = obj as THREE.Mesh;
          if (mesh.geometry?.uuid === uuid) found = mesh;
        }
      });
      if (found) meshCacheRef.current.set(uuid, found);
      return found;
    },
    [modelGroupRef],
  );

  // -------------------------------------------------------------------
  // Project world-space point to pixel coords
  // -------------------------------------------------------------------
  const projectToPixels = useCallback(
    (worldPoint: THREE.Vector3): { x: number; y: number } => {
      const ndc = worldPoint.clone().project(camera);
      return {
        x: (ndc.x * 0.5 + 0.5) * size.width,
        y: (-ndc.y * 0.5 + 0.5) * size.height,
      };
    },
    [camera, size],
  );

  // -------------------------------------------------------------------
  // Main per-frame loop
  // -------------------------------------------------------------------
  useFrame(() => {
    // ---- Programmatic camera-movement detection (fallback) ----
    // Only checked when the user is NOT interacting via OrbitControls.
    // This catches auto-rotate, PiP camera animation, etc.
    const camMat = camera.matrixWorld;

    if (!userInteractingRef.current) {
      const changed = !matricesNearlyEqual(
        prevCamMatrix.current,
        camMat,
        MATRIX_EPS,
      );

      if (changed) {
        enterMoving();
        // Reset the stop timer on every moving frame
        scheduleStopTimer();
      }
    }

    // Always keep prevCamMatrix in sync so we don't get a false delta
    // on the first frame after user interaction ends.
    prevCamMatrix.current.copy(camMat);

    // Skip annotation computation while moving
    if (isMovingRef.current) return;

    if (!enabled || annotatedGeometries.length === 0) {
      if (prevDataRef.current !== '[]') {
        prevDataRef.current = '[]';
        onDataChangeRef.current([]);
      }
      return;
    }

    // ---- Hover raycasting ----
    let hoveredUUID: string | null = null;
    if (modelGroupRef.current) {
      raycaster.current.setFromCamera(pointerRef.current, camera);
      const intersects = raycaster.current.intersectObjects(
        modelGroupRef.current.children,
        true,
      );
      if (intersects.length > 0) {
        hoveredUUID =
          (intersects[0].object as THREE.Mesh).geometry?.uuid ?? null;
      }
    }

    const result: AnnotationRenderData[] = annotatedGeometries.map((cg) => {
      const { geometry, annotation } = cg;
      const empty: AnnotationRenderData = {
        name: annotation?.name ?? '',
        description: annotation?.description ?? '',
        alwaysVisible: annotation?.alwaysVisible ?? false,
        visible: false,
        hovered: false,
        screenBounds: { left: 0, top: 0, right: 0, bottom: 0 },
      };

      if (!annotation) return empty;

      const mesh = findMeshByUUID(geometry.uuid);
      if (!mesh) return empty;

      let localBbox = bboxCache.current.get(geometry.uuid);
      if (!localBbox) {
        geometry.computeBoundingBox();
        if (!geometry.boundingBox || geometry.boundingBox.isEmpty())
          return empty;
        localBbox = geometry.boundingBox.clone();
        bboxCache.current.set(geometry.uuid, localBbox);
      }

      const { min, max } = localBbox;
      const localCorners = [
        new THREE.Vector3(min.x, min.y, min.z),
        new THREE.Vector3(max.x, min.y, min.z),
        new THREE.Vector3(min.x, max.y, min.z),
        new THREE.Vector3(max.x, max.y, min.z),
        new THREE.Vector3(min.x, min.y, max.z),
        new THREE.Vector3(max.x, min.y, max.z),
        new THREE.Vector3(min.x, max.y, max.z),
        new THREE.Vector3(max.x, max.y, max.z),
      ];

      mesh.updateWorldMatrix(true, false);
      const matrixWorld = mesh.matrixWorld;

      const projected = localCorners.map((c) =>
        projectToPixels(c.applyMatrix4(matrixWorld)),
      );

      let minX = Infinity,
        maxX = -Infinity;
      let minY = Infinity,
        maxY = -Infinity;
      for (const p of projected) {
        if (p.x < minX) minX = p.x;
        if (p.x > maxX) maxX = p.x;
        if (p.y < minY) minY = p.y;
        if (p.y > maxY) maxY = p.y;
      }
      const fullArea = Math.max(0, maxX - minX) * Math.max(0, maxY - minY);

      const clLeft = Math.max(0, minX);
      const clTop = Math.max(0, minY);
      const clRight = Math.min(size.width, maxX);
      const clBottom = Math.min(size.height, maxY);
      const clippedArea =
        Math.max(0, clRight - clLeft) * Math.max(0, clBottom - clTop);

      const visible =
        fullArea > 0 && clippedArea / fullArea >= VISIBILITY_THRESHOLD;
      const hovered = hoveredUUID === geometry.uuid;

      return {
        name: annotation.name,
        description: annotation.description,
        alwaysVisible: annotation.alwaysVisible,
        visible,
        hovered,
        screenBounds: {
          left: clLeft,
          top: clTop,
          right: clRight,
          bottom: clBottom,
        },
      };
    });

    const serialised = JSON.stringify(result);
    if (serialised === prevDataRef.current) return;
    prevDataRef.current = serialised;
    onDataChangeRef.current(result);
  });

  return null;
}
